import { albumByIdQuery, albumCreate } from '@/entities/albums'
import {
  $lastCreatedAuthor,
  type AuthorsQueryModelWithPagination,
  createAuthorsQueryModel,
} from '@/entities/authors'
import { type AuthorPreview } from '@/entities/authors'
import { type AlbumAuthorModel, type AlbumModel } from '@/shared/api'
import { combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

interface AlbumCommonInformationFormGateProps {
  initialAuthor?: AuthorPreview
}
export const AlbumCommonInformationFormGate =
  createGate<AlbumCommonInformationFormGateProps>()

const $albumCommonInformationFormAlbumData = createStore<AlbumModel | null>(
  null,
)
export const albumCommonInformationFormAlbumDataChanged =
  createEvent<AlbumModel | null>()
$albumCommonInformationFormAlbumData.on(
  albumCommonInformationFormAlbumDataChanged,
  (_, album) => album,
)

// Authors

export const $albumCommonInformationFormInitialAuthor = createStore<{
  id: string
  nickname: string
} | null>(null)
  .on(
    AlbumCommonInformationFormGate.open,
    (_, state) => state.initialAuthor ?? null,
  )
  .on(AlbumCommonInformationFormGate.close, () => null)

export const authorsAlbumCommonInformationFormAuthorsQueryModel =
  createAuthorsQueryModel({
    usePagination: true,
    accumulate: true,
  }) as AuthorsQueryModelWithPagination

const {
  availableSet: authorsAlbumCommonInformationFormAvailableSet,
  unavailableSet: authorsAlbumCommonInformationFormUnavailableSet,
} = authorsAlbumCommonInformationFormAuthorsQueryModel
export const {
  //   Query
  query: authorsAlbumCommonInformationFormQuery,
  //   Init
  filtersInit: authorsAlbumCommonInformationFormFiltersInit,
  // Pagination
  pageIncreased: authorsAlbumCommonInformationFormPageIncreased,
  pageSizeChanged: authorsAlbumCommonInformationFormPageSizeChanged,
  //   Search
  $search: $authorsAlbumCommonInformationFormSearch,
  searchChanged: authorsAlbumCommonInformationFormSearchChanged,
  searchReset: authorsAlbumCommonInformationFormSearchReset,
  //   Refresh
  refreshed: authorsAlbumCommonInformationFormRefreshed,
  // Accumulated
  $accumulatedData: $authorsAlbumCommonInformationFormAccumulatedQueryData,
} = authorsAlbumCommonInformationFormAuthorsQueryModel

sample({
  clock: AlbumCommonInformationFormGate.open,
  target: authorsAlbumCommonInformationFormAvailableSet,
})
sample({
  clock: AlbumCommonInformationFormGate.close,
  target: authorsAlbumCommonInformationFormUnavailableSet,
})

sample({
  source: AlbumCommonInformationFormGate.state,
  clock: authorsAlbumCommonInformationFormAvailableSet,
  target: authorsAlbumCommonInformationFormRefreshed,
})

export const $albumCommonInformationFormAuthors = combine(
  $authorsAlbumCommonInformationFormAccumulatedQueryData,
  $albumCommonInformationFormAlbumData,
  $lastCreatedAuthor,
  $albumCommonInformationFormInitialAuthor,
  (
    fetchedCoauthors,
    album,
    lastCreatedAuthor,
    initialAuthor,
  ): AlbumAuthorModel[] => {
    const combined = [
      ...fetchedCoauthors.map((a) => ({
        id: a.id,
        nickname: a.nickname,
        isCoauthor: false,
      })),
      ...(album ? album.authors.filter((a) => !a.isCoauthor) : []),
      ...(lastCreatedAuthor
        ? [
            {
              id: lastCreatedAuthor.id,
              nickname: lastCreatedAuthor.nickname,
              isCoauthor: false,
            },
          ]
        : []),
      ...(initialAuthor ? [{ ...initialAuthor, isCoauthor: false }] : []),
    ]

    const uniqueMap = new Map<string, AlbumAuthorModel>()
    for (const author of combined) {
      uniqueMap.set(author.id, author)
    }

    return Array.from(uniqueMap.values())
  },
)

// Coauthors

export const coauthorsAlbumCommonInformationFormAuthorsQueryModel =
  createAuthorsQueryModel({
    usePagination: true,
    accumulate: true,
  }) as AuthorsQueryModelWithPagination
const {
  availableSet: coauthorsAlbumCommonInformationFormAvailableSet,
  unavailableSet: coauthorsAlbumCommonInformationFormUnavailableSet,
} = coauthorsAlbumCommonInformationFormAuthorsQueryModel
export const {
  //   Query
  query: coauthorsAlbumCommonInformationFormQuery,
  //   Init
  filtersInit: coauthorsAlbumCommonInformationFormFiltersInit,
  // Pagination
  pageIncreased: coauthorsAlbumCommonInformationFormPageIncreased,
  pageSizeChanged: coauthorsAlbumCommonInformationFormPageSizeChanged,
  //   Search
  $search: $coauthorsAlbumCommonInformationFormSearch,
  searchChanged: coauthorsAlbumCommonInformationFormSearchChanged,
  searchReset: coauthorsAlbumCommonInformationFormSearchReset,
  //   Refresh
  refreshed: coauthorsAlbumCommonInformationFormRefreshed,
  // Accumulated
  $accumulatedData: $coauthorsAlbumCommonInformationFormAccumulatedQueryData,
} = coauthorsAlbumCommonInformationFormAuthorsQueryModel

sample({
  clock: AlbumCommonInformationFormGate.open,
  target: coauthorsAlbumCommonInformationFormAvailableSet,
})
sample({
  clock: AlbumCommonInformationFormGate.close,
  target: coauthorsAlbumCommonInformationFormUnavailableSet,
})
sample({
  clock: coauthorsAlbumCommonInformationFormAvailableSet,
  target: coauthorsAlbumCommonInformationFormRefreshed,
})

export const $albumCommonInformationFormCoauthors = combine(
  $coauthorsAlbumCommonInformationFormAccumulatedQueryData,
  $albumCommonInformationFormAlbumData,
  $lastCreatedAuthor,
  (fetchedCoauthors, album, lastCreatedAuthor): AlbumAuthorModel[] => {
    const combined = [
      ...fetchedCoauthors.map((a) => ({
        id: a.id,
        nickname: a.nickname,
        isCoauthor: false,
      })),
      ...(album ? album.authors.filter((a) => a.isCoauthor) : []),
      ...(lastCreatedAuthor
        ? [
            {
              id: lastCreatedAuthor.id,
              nickname: lastCreatedAuthor.nickname,
              isCoauthor: false,
            },
          ]
        : []),
    ]

    const uniqueMap = new Map<string, AlbumAuthorModel>()
    for (const author of combined) {
      uniqueMap.set(author.id, author)
    }

    return Array.from(uniqueMap.values())
  },
)

// Check for form data load

const $formFieldsQueryData = combine(
  [
    authorsAlbumCommonInformationFormQuery.$data,
    coauthorsAlbumCommonInformationFormQuery.$data,
  ],
  (data) => data,
)

const $formFieldsQueryStatuses = combine(
  [
    authorsAlbumCommonInformationFormQuery.$status,
    coauthorsAlbumCommonInformationFormQuery.$status,
  ],
  (statuses) => statuses,
)

export const $isAlbumCommonInformationFormDataPending = combine(
  albumByIdQuery.$data,
  albumByIdQuery.$status,
  $formFieldsQueryData,
  $formFieldsQueryStatuses,
  (albumByIdQueryData, albumByIdQueryStatus, otherData, otherStatuses) => {
    if (!albumByIdQueryData && albumByIdQueryStatus === 'initial') return true
    return otherData.some((data, i) => {
      if (otherStatuses[i] === 'initial') return false
      return !data
    })
  },
)

//
export const lastCreatedAlbumIdReset = createEvent()
export const $lastCreatedAlbumId = createStore<string | null>(null)
  .on(albumCreate.finished.success, (_, data) => data.result.id)
  .on(lastCreatedAlbumIdReset, () => null)

sample({
  clock: albumCreate.finished.success,
  target: [albumCreate.reset],
})
